import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, ExternalLink, Copy, Clock, Link as LinkIcon, Video } from "lucide-react";
import { Avatar, Badge, Button, CalendarMonthSkeleton, CalendarRowsSkeleton, DateTimePicker, EmptyState, Input, Label, Modal, ModalFooter, Select, useConfirm, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { formatTime, formatDate, formatLocalDateKey } from "@/lib/format";
import { cn } from "@/lib/utils";
import { SmartListPicker } from "@/features/lists/SmartListPicker";
import { useSmartList, smartListQueryParams } from "@/features/lists/SmartListContext";
import type { Appointment, Calendar } from "@/lib/types";

export function CalendarPage() {
  const toast = useToast();
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [calendarFilter, setCalendarFilter] = useState<string | "all">("all");
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [cursor, setCursor] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [view, setView] = useState<"month" | "week" | "day">("month");
  const [selectedAppt, setSelectedAppt] = useState<Appointment | null>(null);
  const { listId, filter: smartFilter, set: setSmartList } = useSmartList();

  // Compute the visible date range based on view.
  const range = useMemo(() => {
    if (view === "month") {
      const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const end = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
      return { start, end };
    }
    if (view === "week") {
      const d = new Date(cursor);
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - d.getDay());
      const end = new Date(d);
      end.setDate(end.getDate() + 7);
      return { start: d, end };
    }
    const d = new Date(cursor);
    d.setHours(0, 0, 0, 0);
    const end = new Date(d);
    end.setDate(end.getDate() + 1);
    return { start: d, end };
  }, [cursor, view]);

  const loadCalendars = async () => {
    try {
      const d = await api.get<{ calendars: Calendar[] }>("/calendars");
      setCalendars(d.calendars);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    }
  };
  const loadAppointments = async () => {
    setLoading(true);
    try {
      const d = await api.get<{ appointments: Appointment[] }>("/appointments", {
        from: range.start.toISOString(),
        to: range.end.toISOString(),
        ...smartListQueryParams(listId, smartFilter),
      });
      setAppointments(d.appointments);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { void loadCalendars(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { void loadAppointments(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [range.start.getTime(), range.end.getTime(), listId, smartFilter]);

  const filtered = appointments.filter((a) => calendarFilter === "all" || a.calendar_id === calendarFilter);

  const goPrev = () => {
    const d = new Date(cursor);
    if (view === "month") d.setMonth(d.getMonth() - 1);
    else if (view === "week") d.setDate(d.getDate() - 7);
    else d.setDate(d.getDate() - 1);
    setCursor(d);
  };
  const goNext = () => {
    const d = new Date(cursor);
    if (view === "month") d.setMonth(d.getMonth() + 1);
    else if (view === "week") d.setDate(d.getDate() + 7);
    else d.setDate(d.getDate() + 1);
    setCursor(d);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <div className="flex flex-col gap-3 border-b border-zinc-800/70 px-4 py-3 sm:flex-row sm:items-center sm:px-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={goPrev} aria-label="Previous"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" onClick={goNext} aria-label="Next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date())}>Today</Button>
          <h1 className="ml-2 text-lg font-semibold text-zinc-100 sm:ml-3 sm:text-xl">
            {view === "month"
              ? cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })
              : view === "week"
                ? `Week of ${formatDate(range.start, { month: "short", day: "numeric" })}`
                : formatDate(cursor, { weekday: "long", month: "long", day: "numeric" })}
          </h1>
        </div>
        <div className="flex flex-1 items-center gap-2 sm:justify-end">
          <div className="hidden sm:inline-flex rounded-lg border border-zinc-800 p-0.5">
            {(["month", "week", "day"] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={cn(
                  "px-2.5 h-7 rounded-md text-xs font-medium capitalize transition-colors",
                  view === v ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:text-zinc-200"
                )}
              >
                {v}
              </button>
            ))}
          </div>
          <Select value={calendarFilter} onChange={(e) => setCalendarFilter(e.target.value as typeof calendarFilter)} className="sm:w-44">
            <option value="all">All calendars</option>
            {calendars.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <SmartListPicker
            listId={listId}
            filter={smartFilter}
            onChange={(lid, f) => setSmartList(lid, f)}
          />
          <Link to="/scheduling-links">
            <Button variant="outline" size="sm" leftIcon={<LinkIcon className="h-4 w-4" />}>
              <span className="hidden sm:inline">Booking Links</span>
            </Button>
          </Link>
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)}>New</Button>
        </div>
      </div>

      {/* Calendar body — fills remaining height */}
      <div className="flex-1 overflow-hidden">
        {loading && appointments.length === 0 ? (
          view === "month" ? <CalendarMonthSkeleton /> : <CalendarRowsSkeleton />
        ) : view === "month" ? (
          <MonthView cursor={cursor} appointments={filtered} onSelect={setSelectedAppt} />
        ) : view === "week" ? (
          <WeekView range={range} appointments={filtered} onSelect={setSelectedAppt} />
        ) : (
          <DayView day={cursor} appointments={filtered} onSelect={setSelectedAppt} />
        )}
      </div>

      <AppointmentDialog open={createOpen} onClose={() => setCreateOpen(false)} calendars={calendars} onSaved={() => loadAppointments()} />
      <AppointmentDetailDialog
        appointment={selectedAppt}
        onClose={() => setSelectedAppt(null)}
        onSaved={() => { setSelectedAppt(null); loadAppointments(); }}
      />
    </div>
  );
}

function colorClass(color?: string | null): string {
  const map: Record<string, string> = {
    indigo:  "bg-rose-500/20 text-rose-200 border-l-rose-400",
    blue:    "bg-sky-500/20 text-sky-200 border-l-sky-400",
    emerald: "bg-emerald-500/20 text-emerald-200 border-l-emerald-400",
    amber:   "bg-amber-500/20 text-amber-200 border-l-amber-400",
    rose:    "bg-rose-500/20 text-rose-200 border-l-rose-400",
    violet:  "bg-rose-500/20 text-rose-200 border-l-rose-400",
    zinc:    "bg-zinc-700/40 text-zinc-200 border-l-zinc-500",
  };
  return map[color ?? "indigo"] ?? map.indigo!;
}

// ─── Month view ───────────────────────────────────────────────────────────
function MonthView({ cursor, appointments, onSelect }: { cursor: Date; appointments: Appointment[]; onSelect: (a: Appointment) => void }) {
  const days = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = new Date(first);
    start.setDate(start.getDate() - start.getDay());
    const out: { date: Date; isCurrentMonth: boolean }[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      out.push({ date: d, isCurrentMonth: d.getMonth() === cursor.getMonth() });
    }
    return out;
  }, [cursor]);

  const apptsByDay = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    for (const a of appointments) {
      const d = formatLocalDateKey(new Date(a.starts_at));
      const list = map.get(d) ?? [];
      list.push(a);
      map.set(d, list);
    }
    for (const list of map.values()) list.sort((x, y) => +new Date(x.starts_at) - +new Date(y.starts_at));
    return map;
  }, [appointments]);

  const todayKey = formatLocalDateKey(new Date());

  return (
    <div className="flex h-full flex-col">
      <div className="grid grid-cols-7 border-b border-zinc-800/70">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
          <div key={d} className="px-3 py-2 text-center text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{d}</div>
        ))}
      </div>
      <div className="grid flex-1 grid-cols-7 grid-rows-6 gap-px bg-zinc-800/40">
        {days.map(({ date, isCurrentMonth }, i) => {
          const dayKey = formatLocalDateKey(date);
          const list = apptsByDay.get(dayKey) ?? [];
          const isToday = dayKey === todayKey;
          return (
            <div
              key={i}
              className={cn(
                "flex min-h-[5rem] flex-col bg-zinc-950 p-1.5 transition-colors hover:bg-zinc-900/40",
                !isCurrentMonth && "bg-zinc-950/60",
              )}
            >
              <div className="mb-1 flex items-center justify-between">
                <span
                  className={cn(
                    "text-xs font-medium",
                    !isCurrentMonth && "text-zinc-700",
                    isCurrentMonth && !isToday && "text-zinc-400",
                    isToday && "inline-flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-white"
                  )}
                >
                  {date.getDate()}
                </span>
              </div>
              <div className="space-y-0.5">
                {list.slice(0, 3).map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => onSelect(a)}
                    className={cn(
                      "block w-full truncate rounded border-l-2 px-1.5 py-0.5 text-left text-[10px] font-medium hover:brightness-125",
                      colorClass(a.calendar_color)
                    )}
                  >
                    <span className="opacity-70">{formatTime(a.starts_at)}</span> {a.title}
                  </button>
                ))}
                {list.length > 3 && (
                  <span className="block px-1 text-[10px] text-zinc-500">+{list.length - 3} more</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Week view ─────────────────────────────────────────────────────────────
function WeekView({ range, appointments, onSelect }: { range: { start: Date; end: Date }; appointments: Appointment[]; onSelect: (a: Appointment) => void }) {
  const days = useMemo(() => {
    const out: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(range.start);
      d.setDate(d.getDate() + i);
      out.push(d);
    }
    return out;
  }, [range.start]);

  const apptsByDay = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    for (const a of appointments) {
      const d = formatLocalDateKey(new Date(a.starts_at));
      const list = map.get(d) ?? [];
      list.push(a);
      map.set(d, list);
    }
    for (const list of map.values()) list.sort((x, y) => +new Date(x.starts_at) - +new Date(y.starts_at));
    return map;
  }, [appointments]);

  const todayKey = formatLocalDateKey(new Date());

  return (
    <div className="grid h-full grid-cols-1 sm:grid-cols-7 gap-px bg-zinc-800/40 overflow-y-auto">
      {days.map((date) => {
        const dayKey = formatLocalDateKey(date);
        const list = apptsByDay.get(dayKey) ?? [];
        const isToday = dayKey === todayKey;
        return (
          <div key={dayKey} className="flex flex-col bg-zinc-950 min-h-32">
            <div className={cn("border-b border-zinc-800/70 px-3 py-2 text-center", isToday && "bg-rose-500/10")}>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                {date.toLocaleDateString(undefined, { weekday: "short" })}
              </div>
              <div className={cn("mt-0.5 text-lg font-semibold", isToday ? "text-rose-300" : "text-zinc-200")}>
                {date.getDate()}
              </div>
            </div>
            <div className="flex-1 space-y-1 p-2">
              {list.length === 0 ? (
                <p className="text-[10px] text-zinc-700 text-center mt-3">—</p>
              ) : list.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => onSelect(a)}
                  className={cn(
                    "block w-full rounded border-l-2 px-2 py-1 text-left text-xs hover:brightness-125",
                    colorClass(a.calendar_color)
                  )}
                >
                  <div className="font-medium truncate">{a.title}</div>
                  <div className="text-[10px] opacity-80">{formatTime(a.starts_at)}</div>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Day view ──────────────────────────────────────────────────────────────
function DayView({ day, appointments, onSelect }: { day: Date; appointments: Appointment[]; onSelect: (a: Appointment) => void }) {
  const dayKey = formatLocalDateKey(day);
  const list = appointments.filter((a) => formatLocalDateKey(new Date(a.starts_at)) === dayKey)
    .sort((x, y) => +new Date(x.starts_at) - +new Date(y.starts_at));

  // Render hour grid 6am-10pm.
  const hours = Array.from({ length: 17 }, (_, i) => i + 6);

  return (
    <div className="mx-auto h-full max-w-3xl overflow-y-auto px-4 py-6">
      {list.length === 0 ? (
        <div className="py-16 text-center">
          <CalendarIcon className="mx-auto h-8 w-8 text-zinc-700" />
          <p className="mt-3 text-sm text-zinc-500">No appointments today</p>
        </div>
      ) : (
        <div className="space-y-px">
          {hours.map((hour) => {
            const slot = list.filter((a) => new Date(a.starts_at).getHours() === hour);
            return (
              <div key={hour} className="grid grid-cols-[3.5rem_1fr] gap-2 border-t border-zinc-800/40 py-2">
                <div className="pt-0.5 text-right text-[10px] font-medium uppercase tracking-wider text-zinc-500">
                  {hour === 0 ? "12 AM" : hour < 12 ? `${hour} AM` : hour === 12 ? "12 PM" : `${hour - 12} PM`}
                </div>
                <div className="space-y-1">
                  {slot.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => onSelect(a)}
                      className={cn(
                        "block w-full rounded-lg border-l-2 px-3 py-2 text-left text-sm hover:brightness-125",
                        colorClass(a.calendar_color)
                      )}
                    >
                      <div className="font-semibold">{a.title}</div>
                      <div className="text-[11px] opacity-80 mt-0.5">
                        {formatTime(a.starts_at)} – {formatTime(a.ends_at)}
                        {a.contact_name && ` · ${a.contact_name}`}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AppointmentDetailDialog({ appointment, onClose, onSaved }: { appointment: Appointment | null; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();
  if (!appointment) return null;
  const onCancel = async () => {
    if (!(await confirm({
      title: "Cancel this appointment?",
      description: "The contact won't be notified automatically — send them a message yourself if needed.",
      confirmLabel: "Cancel appointment",
      cancelLabel: "Keep it",
      danger: true,
    }))) return;
    try {
      await api.patch(`/appointments/${appointment.id}`, { status: "cancelled" });
      toast.success("Cancelled");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    }
  };
  const onComplete = async () => {
    try {
      await api.patch(`/appointments/${appointment.id}`, { status: "completed" });
      toast.success("Marked completed");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    }
  };
  return (
    <Modal open onClose={onClose} title={appointment.title} size="md">
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-zinc-300">
          <Clock className="h-4 w-4 text-zinc-500" />
          {formatDate(appointment.starts_at, { weekday: "long", month: "long", day: "numeric" })}
          <span className="text-zinc-500">·</span>
          {formatTime(appointment.starts_at)} – {formatTime(appointment.ends_at)}
        </div>
        {appointment.contact_name && (
          <div className="flex items-center gap-2 text-sm">
            <Avatar name={appointment.contact_name} size="xs" />
            <span className="text-zinc-300">{appointment.contact_name}</span>
          </div>
        )}
        {appointment.description && <p className="text-sm text-zinc-400 whitespace-pre-wrap">{appointment.description}</p>}
        {appointment.meeting_url && (
          <a
            href={appointment.meeting_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-indigo-300 hover:text-indigo-200"
          >
            <Video className="h-4 w-4" />
            Join video call
          </a>
        )}
        <div className="text-xs text-zinc-500">
          Status: <Badge color={appointment.status === "completed" ? "emerald" : appointment.status === "cancelled" ? "rose" : "indigo"}>{appointment.status}</Badge>
          {appointment.sync_event_id && !appointment.sync_error && (
            <span className="ml-2 text-emerald-400/80">On the linked calendar</span>
          )}
        </div>
        {appointment.sync_error && (
          <p className="text-xs text-amber-400/90">Calendar sync: {appointment.sync_error}</p>
        )}
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Close</Button>
        {appointment.status !== "cancelled" && appointment.status !== "completed" && (
          <>
            <Button variant="outline" onClick={onCancel}>Cancel appointment</Button>
            <Button onClick={onComplete}>Mark completed</Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}

function AppointmentDialog({
  open, onClose, calendars, onSaved,
}: { open: boolean; onClose: () => void; calendars: Calendar[]; onSaved: () => void }) {
  const toast = useToast();
  const [calendarId, setCalendarId] = useState<string>("");
  const [title, setTitle] = useState("");
  const [starts, setStarts] = useState("");
  const [ends, setEnds] = useState("");
  const [contactQuery, setContactQuery] = useState("");
  const [contactId, setContactId] = useState<string | null>(null);
  const [contactOptions, setContactOptions] = useState<{ id: string; full_name: string | null; email: string | null }[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCalendarId(calendars[0]?.id ?? "");
      setTitle(""); setStarts(""); setEnds(""); setContactQuery(""); setContactId(null);
    }
  }, [open, calendars]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!contactQuery.trim() || contactId) return;
      api.get<{ contacts: { id: string; full_name: string | null; email: string | null }[] }>("/contacts", { q: contactQuery, limit: 8 })
        .then((d) => setContactOptions(d.contacts))
        .catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [contactQuery, contactId]);

  const submit = async () => {
    if (!title.trim() || !starts || !ends) return;
    setSaving(true);
    try {
      await api.post("/appointments", {
        calendar_id: calendarId || null,
        title, starts_at: new Date(starts).toISOString(),
        ends_at: new Date(ends).toISOString(),
        contact_id: contactId,
      });
      toast.success("Appointment created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="New appointment" size="md">
      <div className="space-y-3">
        <div><Label required>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus /></div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label required>Starts</Label><DateTimePicker value={starts} onChange={setStarts} placeholder="Pick a start" /></div>
          <div><Label required>Ends</Label><DateTimePicker value={ends} onChange={setEnds} placeholder="Pick an end" /></div>
        </div>
        {calendars.length > 0 && (
          <div>
            <Label>Calendar</Label>
            <Select value={calendarId} onChange={(e) => setCalendarId(e.target.value)}>
              <option value="">— None —</option>
              {calendars.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </div>
        )}
        <div>
          <Label>Contact (optional)</Label>
          <Input value={contactQuery} onChange={(e) => { setContactQuery(e.target.value); setContactId(null); }} placeholder="Search contact…" />
          {contactOptions.length > 0 && (
            <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-950">
              {contactOptions.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { setContactId(c.id); setContactQuery(c.full_name ?? c.email ?? ""); setContactOptions([]); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-zinc-900"
                >
                  <Avatar name={c.full_name ?? c.email ?? "?"} size="xs" />
                  <span className="truncate text-zinc-200">{c.full_name ?? c.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving}>Create</Button>
      </ModalFooter>
    </Modal>
  );
}

